<script setup lang="ts">
import { computed } from 'vue';

import { assertValue } from '@libs/utils';

import AppButton from '../../appButton/AppButton.vue';

export interface AppMasonryGalleryProps {
  fullGalleryButtonText: string;
  imageUrls: string[];
}

const props = defineProps<AppMasonryGalleryProps>();

interface AppMasonryGalleryEmits {
  (eventName: 'showFullGallery'): void;
}

const emit = defineEmits<AppMasonryGalleryEmits>();

const FULL_GALLERY_THRESHOLD = 5;

const showFullGallery = computed<boolean>(
  () => props.imageUrls.length < FULL_GALLERY_THRESHOLD
);

const firstImageUrl = computed<string>(() => {
  const imageUrl = props.imageUrls[0];
  assertValue(
    imageUrl,
    'ui > AppMansoryGallery.vue > firstImageUrl = computed > imageUrl'
  );

  return imageUrl;
});

function getBackgroundImageStyle(imageUrl: string): string {
  return `background-image: url('${imageUrl}');`;
}
</script>

<template>
  <div class="app-masonry-gallery">
    <div
      class="main-image"
      :class="showFullGallery ? 'full-space' : 'half-space'"
      :style="getBackgroundImageStyle(firstImageUrl)"
    />
    <div class="small-images-container">
      <div
        v-for="(imageUrl, index) in props.imageUrls.slice(
          1,
          FULL_GALLERY_THRESHOLD
        )"
        :key="index"
        class="thumbnail-image"
        :style="getBackgroundImageStyle(imageUrl)"
      />
      <AppButton
        type="secondary"
        state="enabled"
        size="medium"
        :icon="{ name: 'gallery' }"
        class="show-gallery-button"
        @click="emit('showFullGallery')"
      >
        {{ props.fullGalleryButtonText }}
      </AppButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.app-masonry-gallery {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  border-radius: var(--border-radius-12);
  overflow: hidden;

  .main-image {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    &.full-space {
      flex: 0 0 100%;
    }

    &.half-space {
      flex: 0 0 50%;
    }
  }

  .small-images-container {
    --gallery-spacing: var(--spacing-8);
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    grid-gap: var(--gallery-spacing);
    margin-left: var(--gallery-spacing);
    width: 100%;

    .thumbnail-image {
      background-position: top;
      background-size: cover;
      background-repeat: no-repeat;
    }

    .show-gallery-button {
      position: absolute;
      bottom: var(--spacing-16);
      right: var(--spacing-16);
    }
  }
}
</style>
